
import { cloneDeep, get, set } from 'lodash';
import { toRaw } from 'vue';
import { Options, Vue } from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import NCheckbox from '../checkbox/NCheckbox.vue';
import NCheckboxIcon from '../checkbox/NCheckboxIcon.vue';
import { AnyFunction, IFormContext, IFormError, IFormLayoutItem, IFormModel } from '../forms/NForm.vue';
import NFormError from '../forms/NFormError.vue';
import NHint from '../hint/NHint.vue';
import NTooltip from '../hint/NTooltip.vue';
import { translateProps } from '../utils';

@Options({
  name: 'NFormItem',
  components: { NCheckbox, NCheckboxIcon, NFormError, NHint, NTooltip }
})
export default class NFormItem extends Vue {
  @Prop({ type: Object, required: true })
  readonly item!: IFormLayoutItem;

  @Prop({ type: Object, required: true })
  readonly model!: IFormModel;

  @Prop({ type: Object, default: null })
  readonly context!: IFormContext;

  @Prop({ type: Boolean, default: false })
  readonly disabled!: boolean;

  @Prop({ type: Object, default: null })
  readonly error!: IFormError;

  @Prop({ type: Boolean, default: false })
  readonly hasEnabledFields!: boolean;

  @Prop({ type: Boolean, default: false })
  readonly isFieldEnabled!: boolean;

  @Prop({ type: String })
  readonly dataQa?: string;

  reference: any = null;

  referenceChange(reference: any) {
    this.reference = reference;
  }

  get fieldDataQa() {
    const elementSuffix = this.item.dataQa || this.item.name || this.item.path;
    return `${this.dataQa}.${elementSuffix}`;
  }

  get fieldId() {
    return this.item.path || this.item.name;
  }

  get itemContext(): IFormContext {
    return { ...this.context, item: this.item };
  }

  get label() {
    if (this.item.i18n_label) return this.$t(this.item.i18n_label, 'f');
    return this.item.label;
  }

  get tooltip() {
    if (this.item.i18n_tooltip) return this.$t(this.item.i18n_tooltip, 'f');
    return this.item.tooltip;
  }

  get tooltipPlacement() {
    return this.item.tooltipPlacement ?? 'top-right';
  }

  get errorPlacements() {
    return this.item.errorPlacements;
  }

  get errorStyles() {
    return this.item.errorStyles;
  }

  get component() {
    return toRaw(this.item.component);
  }

  get isUseModel(): boolean {
    return !!(this.item.path || this.item.encode || this.item.decode);
  }

  get modelValue() {
    if (this.item.decode instanceof Function) {
      return this.item.decode.call(this.itemContext, this.model);
    }
    return get(this.model, this.item.path || 'undefined');
  }

  set modelValue(value) {
    if (this.item.encode instanceof Function) {
      this.item.encode.call(this.itemContext, this.model, value);
      return;
    }
    set(this.model, this.item.path || 'undefined', value);
  }

  get handlers() {
    const handlers: Record<string, AnyFunction> = {};
    if (this.item.on instanceof Object) {
      Object.keys(this.item.on).forEach((name: string) => {
        handlers[name] = (...args: any[]) => {
          this.item.on![name].apply(this.itemContext, args);
        };
      });
    }
    return handlers;
  }

  get props() {
    // todo: refactor it!
    const originalProps = this.item.props instanceof Function ? this.item.props.call(this.itemContext, this.model) : this.item.props;

    const resultProps = originalProps ? cloneDeep(originalProps) : { disabled: this.disabled };
    if (originalProps) {
      if (!Object.isFrozen(originalProps)) resultProps.disabled = this.disabled;
      if (originalProps.model) resultProps.model = originalProps.model;
      if (originalProps.item) resultProps.item = originalProps.item;
    }
    return translateProps(resultProps, this.$t);
  }

  get hidden() {
    if (this.item.hidden instanceof Function) {
      return this.item.hidden(this.model);
    }
    return this.item.hidden;
  }

  get canFocus(): boolean {
    return this.$refs?.control?.focus && !!this.$refs?.control?.focus;
  }

  focusOnControl(): void {
    setTimeout(() => {
      this.$refs?.control?.focus && this.$refs?.control?.focus();
    }, 0);
  }

  labelClickHandler(e: MouseEvent) {
    this.focusOnControl();
  }
}
